import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { logger } from '@app/libs/datadog';

export function usePDF() {
  return useContext(PDFContext);
}

export function PDFProvider({ children, pdf = null }: { children: React.ReactNode; pdf?: PDF }) {
  const [pdfArrayBuffer, setPDFArrayBuffer] = useState<PDF>(pdf);
  const nextValue = useRef(pdf);

  const resetPDF = useCallback(() => {
    setPDFArrayBuffer(null);
    nextValue.current = null;
  }, []);

  const setPDF = useCallback(
    (pdf: PDF) => {
      const ERROR_MESSAGE = 'Should reset pdfArrayBuffer before set new pdfArrayBuffer';

      if (nextValue.current) {
        if (process.env.REACT_APP_MODUSIGN_ENV === 'production') {
          /**
           * production 환경에서는 cleanup이 안되어있을경우 클린업을 통해 null로 세팅 되었다가 값이 세팅 되도록 한다.
           * PDFContainer를 다시 마운트하기 위해서이다.
           * PDFContainer는 마운트 과정에서 절차적인 처리가 있어서, PDF를 변경했을 때 다시 마운트를 해줘야한다.
           */
          resetPDF();
          logger.error(ERROR_MESSAGE);
        }

        if (process.env.REACT_APP_MODUSIGN_ENV !== 'production') {
          throw new Error(ERROR_MESSAGE);
        }
      }

      setPDFArrayBuffer(pdf);
      nextValue.current = pdf;
    },
    [resetPDF]
  );

  const value = useMemo(
    () => ({
      pdf: pdfArrayBuffer,
      resetPDF,
      setPDF,
    }),
    [pdfArrayBuffer, resetPDF, setPDF]
  );

  return <PDFContext.Provider value={value}>{children}</PDFContext.Provider>;
}

type PDF = ArrayBuffer | null;

/**
 * pdf url을 바로 소비하지 않는 이유는, url에는 생명주기가 있기 때문에 일정 시간이 지나면 pdf url이 만료되기 때문이다.
 * 이를 방지하고자 binary 파일을 메모리에 로드시켜둔다.
 */
const PDFContext = createContext<{
  pdf: PDF;
  resetPDF: () => void;
  setPDF: (pdf: PDF) => void;
}>({
  pdf: null,
  resetPDF: () => {},
  setPDF: () => {},
});
